import { showError } from '@calendly/alert';
import { useFlag } from '@calendly/feature-check-react';
import { I18n } from '@calendly/i18n';
import { useDropdown } from '@calendly/ui/components/dropdown';
import { MailBulk } from '@calendly/ui/components/glyphs/MailBulk';
import { IconButton } from '@calendly/ui/components/icon-button';
import { SettingsMenuVerticalIcon, ExportIcon } from '@calendly/ui/components/icons';
import { Menu, BareItem } from '@calendly/ui/components/menu';
import { MinimalButton } from '@calendly/ui/components/minimal-button';
import { BodyText } from '@calendly/ui/components/typography';
import { colorTextActionContrast } from '@calendly/ui/theme';
import classNames from 'classnames';
import { forwardRef, useContext, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ExportPopup from 'components/export/confirmation';
import { confirmationOpenSelector, closeConfirmation, createDelayedExport } from 'components/export/event_invitees/slice';
import { BOOKING_METHODS } from 'features/one_off_modal';
import { useTrack } from 'hooks/analytics_events';
import { CONTACT_INTERACTION_LOCATIONS } from 'hooks/analytics_events/constants';
import { useOpenEmailComposerFromHost } from 'hooks/oap_web/use_open_email_composer_from_host';
import { isCalendlyMeeting } from 'shared/utils';
import { MeetingDetailDependenciesContext } from '../../../meeting_detail_context';
import { WidgetContainer } from '../widget_container';
import { CalendlyInviteesContent } from './calendly_invitees_content';
import { fetchAllInviteesForBulkEmail } from './collect_bulk_invitee_recipients';
import { EditInviteeLimitButton } from './edit_invitee_limit_button';
import { ExternalEvent } from './external_event';
import { ItemContents } from './item_contents';
import { useInviteesEventId } from './use_invitees_event_id';
const headerContainerIsGroupCss = "h1v0f10h";
const headerContainerCss = "h1lbxen5";
const HeaderContainer = forwardRef((props, ref) => {
  const {
    isGroup,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(headerContainerCss, {
    [headerContainerIsGroupCss]: isGroup
  }, props.className)} />;
});
const styledMenuCss = "s1o08wp6";
const StyledMenu = forwardRef((props, ref) => <Menu ref={ref} {...props} className={classNames(styledMenuCss, props.className)} />);
const styledGroupCss = "s1cht9zy";
const StyledGroup = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(styledGroupCss, props.className)} />;
});
const exportButtonContainerCss = "ejg0quy";
const ExportButtonContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(exportButtonContainerCss, props.className)} />;
});
const exportButtonIconCss = "e7vagbd";
const ExportButton = ({
  event,
  isInMenu
}) => {
  const dispatch = useDispatch();
  const confirmationOpen = useSelector(confirmationOpenSelector);
  const track = useTrack();
  const useStandardIconColor = event?.eventType?.kind !== 'group';
  const handleClick = async () => {
    track({
      name: 'Meeting Interaction',
      properties: {
        action: 'export_attendees',
        location: 'meetings_page_right_rail',
        event_uuid: event?.uuid,
        mi_event_uuid: event?.meetingIntelligence?.uuid,
        is_calendly_meeting: isCalendlyMeeting(event)
      }
    });
    await dispatch(createDelayedExport({
      eventId: event.id
    }));
  };
  if (isInMenu) {
    return <>
        <BareItem onClick={handleClick}>
          <ItemContents>
            <ExportIcon className={useStandardIconColor ? exportButtonIconCss : undefined} />
            <BodyText>{I18n.t('publisher.meetings.invitees.export_attendees')}</BodyText>
          </ItemContents>
        </BareItem>
        {confirmationOpen && <ExportPopup onConfirm={() => dispatch(closeConfirmation())} />}
      </>;
  }
  return <>
      <MinimalButton className={useStandardIconColor ? exportButtonIconCss : undefined} onClick={handleClick} title={I18n.t('publisher.meetings.invitees.export_attendees')} aria-label={I18n.t('publisher.meetings.invitees.export_attendees')}>
        <ExportIcon />
        {!useStandardIconColor && <BodyText color={colorTextActionContrast}>
            {I18n.t('publisher.meetings.invitees.export_attendees')}
          </BodyText>}
      </MinimalButton>
      {confirmationOpen && <ExportPopup onConfirm={() => dispatch(closeConfirmation())} />}
    </>;
};
const headerControlsCss = "h1tjmtg7";
const BulkEmailAttendeesButton = ({
  eventId,
  fetchInvitees,
  isInControlsRow = false,
  skip
}) => {
  const dispatch = useDispatch();
  const openHostEmailComposer = useOpenEmailComposerFromHost();
  const [isFetchingRecipients, setIsFetchingRecipients] = useState(false);
  const handleClick = async () => {
    if (!eventId || skip || isFetchingRecipients) return;
    setIsFetchingRecipients(true);
    try {
      const recipientIds = await fetchAllInviteesForBulkEmail({
        trigger: fetchInvitees,
        eventId
      });
      if (recipientIds.length === 0) {
        dispatch(showError(I18n.t('errors.default_no_action')));
        return;
      }
      openHostEmailComposer({
        recipientIds,
        variant: 'bulk',
        source: 'send_bulk_email',
        sendViaCalendlySource: CONTACT_INTERACTION_LOCATIONS.MEETINGS_PAGE_RIGHT_RAIL
      });
    } catch (error) {
      dispatch(showError(I18n.t('errors.default_no_action')));
    } finally {
      setIsFetchingRecipients(false);
    }
  };
  const label = I18n.t('publisher.meetings.invitees.email_attendees');
  return <IconButton size="small" onClick={handleClick} title={label} aria-label={label} disabled={isFetchingRecipients || skip || !eventId} data-testid={isInControlsRow ? 'bulk-email-attendees-button' : undefined} icon={MailBulk} />;
};
const BulkEmailAttendeesButtonWithInviteesQuery = props => {
  const dependencies = useContext(MeetingDetailDependenciesContext);
  const [fetchInvitees] = dependencies.useLazyGetInviteesQuery();
  return <BulkEmailAttendeesButton {...props} fetchInvitees={fetchInvitees} />;
};
const BulkEmailAttendeesButtonConnected = ({
  event,
  isInControlsRow = false
}) => {
  const dependencies = useContext(MeetingDetailDependenciesContext);
  const isNewEmailComposerEnabled = useFlag('crm_new_email_composer');
  const isGmailSyncEnabled = useFlag('crm_gmail_sync_1_feature');
  const {
    eventId,
    skip,
    isGroup
  } = useInviteesEventId(event);
  const isCalendly = isCalendlyMeeting(event);
  const canFetchInvitees = typeof dependencies?.useLazyGetInviteesQuery === 'function';
  const shouldShow = isCalendly && isGroup && isNewEmailComposerEnabled && isGmailSyncEnabled && eventId && !skip && canFetchInvitees;
  if (!shouldShow) {
    return null;
  }
  return <BulkEmailAttendeesButtonWithInviteesQuery eventId={eventId} isInControlsRow={isInControlsRow} skip={skip} />;
};
const Header = ({
  event
}) => {
  const isGroup = event.eventType?.kind === 'group';
  const isPast = event.past;
  const isPoll = event.eventType?.bookingMethod === BOOKING_METHODS.poll;
  const [toggle, Dropdown, isExpanded] = useDropdown();
  const shouldShowEditInviteeLimit = isGroup && event.policy?.update && !isPoll && !isPast;
  const isCalendly = isCalendlyMeeting(event);
  const getSubtitle = () => {
    if (!isCalendly) return null;
    if (!isGroup) return null;
    const translation = I18n.t('publisher.meetings.invitees.spots_filled', {
      count: event.inviteesLimit,
      filledCount: event.filledCount
    });
    return typeof translation === 'string' ? translation : String(translation);
  };
  const renderControls = () => {
    if (!isCalendly) return null;
    if (shouldShowEditInviteeLimit) {
      return <div className={headerControlsCss}>
          <BulkEmailAttendeesButtonConnected event={event} isInControlsRow />
          <IconButton size="small" onClick={toggle} title={I18n.t('publisher.meetings.invitees.invitees_actions')} aria-label={I18n.t('publisher.meetings.invitees.invitees_actions')} aria-expanded={isExpanded}>
            <SettingsMenuVerticalIcon />
          </IconButton>
          <Dropdown>
            <StyledMenu>
              <StyledGroup>
                <EditInviteeLimitButton event={event} />
                <ExportButton event={event} isInMenu />
              </StyledGroup>
            </StyledMenu>
          </Dropdown>
        </div>;
    }
    return <ExportButtonContainer>
        <BulkEmailAttendeesButtonConnected event={event} isInControlsRow />
        <ExportButton event={event} isInMenu={false} />
      </ExportButtonContainer>;
  };
  const subtitle = getSubtitle();
  return <HeaderContainer isGroup={isGroup}>
      {subtitle && <BodyText color="muted" size="small">{subtitle}</BodyText>}
      {renderControls()}
    </HeaderContainer>;
};

// Single tree for both solo and group: same component and hooks to avoid React render error
// when switching event types. CalendlyInviteesContent uses BaseAttendeeCard for every row.
const CalendlyEvent = ({
  event,
  readonly
}) => <CalendlyInviteesContent key={event.id} event={event} readonly={readonly} />;
export const InviteesWidget = ({
  event,
  readonly
}) => {
  const title = I18n.t('publisher.meetings.invitees.invitees');

  // Don't render if there are no invitees/attendees. This only applies to
  // ExternalEvents (Calendly events always have at least one invitee)
  const shouldHideInviteesWidget = !isCalendlyMeeting(event) && (event?.invitees?.length || 0) === 0 && (event?.eventMemberships?.length || 0) === 0;
  if (shouldHideInviteesWidget) {
    return null;
  }
  return <WidgetContainer title={title} header={<Header event={event} />} noPadding showBorder={false} sectionWithBottomBorder>
      {isCalendlyMeeting(event) ? <CalendlyEvent event={event} readonly={readonly} /> : <ExternalEvent event={event} readonly={readonly} />}
    </WidgetContainer>;
};

require("./invitees_widget.wyw-in-js.css!=!../../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./invitees_widget.js");